import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';

import { useAppSelector } from '@/app/hooks';
import { backendRequestHeaders, getBackendBaseUrl } from '@/api/backendAuth';
import { useAuth } from '@/modules/Auth/hooks/useAuth';
import { selectCurrentWorkspace } from '@/modules/Dashboard/Workspaces/workspaceSlice';

import queryKeys from '../../modules/shared/query/queryKeys';

interface CreateAPICredentialRequest {
  name: string;
  description?: string;
  expirationDays?: number;
}

interface APICredentialResponse {
  id: string;
  name: string;
  description?: string;
  workspaceId: string;
  siteId: string;
  apiKey: string;
  expiresAt: string | null;
  createdAt: string;
  lastUsed: string;
  createdBy: string;
}

interface CreateAPICredentialApiResponse {
  data: APICredentialResponse;
}

/** .NET API: POST /api-credentials { label } → { id, label, keyPrefix, rawKey, createdAt } */
interface BackendCreateResponse {
  id: string;
  label: string | null;
  keyPrefix: string;
  rawKey: string;
  createdAt: string;
  KeyPrefix?: string;
  RawKey?: string;
}

async function createAPICredential(
  data: CreateAPICredentialRequest,
  token: string | undefined,
  workspaceId: string,
): Promise<CreateAPICredentialApiResponse> {
  const response = await axios.post<BackendCreateResponse>(
    `${getBackendBaseUrl()}/api-credentials`,
    { workspaceId, label: data.name },
    { headers: backendRequestHeaders(token) },
  );
  const b = response.data;
  const keyPrefix = b.keyPrefix || b.KeyPrefix || '';
  const rawKey = b.rawKey || b.RawKey || '';
  return {
    data: {
      id: b.id,
      name: b.label ?? data.name,
      description: data.description,
      workspaceId,
      siteId: keyPrefix,
      apiKey: rawKey,
      expiresAt: null,
      createdAt: b.createdAt,
      lastUsed: '',
      createdBy: '',
    },
  };
}

export function useCreateAPICredential() {
  const { user } = useAuth();
  const workspace = useAppSelector(selectCurrentWorkspace);
  const queryClient = useQueryClient();

  return useMutation<CreateAPICredentialApiResponse, Error, CreateAPICredentialRequest>(
    (data) =>
      createAPICredential(data, user?.signInUserSession.idToken.jwtToken, workspace?.id ?? ''),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKeys.apiCredentials, workspace?.id]);
      },
    },
  );
}
